// The Places tab (PoisEditor): its list, its map with pins, its side panel and its JSON import
const en = {
  /** Deletes the item, in its side panel */
  remove: 'Remove',
  title: 'Places',
  subtitle: 'Stages and food spots, pinned on your maps. Drag a pin to move it, click it to edit the place.',
  importJson: 'Import JSON',
  addPlace: 'Add place',
  clearAllMenu: 'Clear all places',
  search: 'Search places...',
  stage: 'Stage',
  stages: 'Stages',
  food: 'Food',
  timetableOrder: 'Timetable order',
  /** The aria-label of a stage's order in the list */
  orderOf: (order: number) => `Timetable order ${order}`,
  noMatch: 'No place matches this search.',
  empty: 'No places here yet.',
  untitledMap: 'Untitled Map',
  notOnMap: 'Not on a map',
  notOnMapCount: (count: number) => `Not on a map · ${count}`,
  noMap: 'No map yet. Add one in the Maps tab to pin places on it.',
  goToMaps: 'Go to Maps',
  mapAlt: 'Festival Map',
  unattributed: 'Places with no map, or whose map was removed, are listed here. Choose a map for a place by editing it.',
  /** The name a new place starts with */
  newPlace: 'New Place',
  addTitle: 'Add place',
  editTitle: 'Edit place',
  name: 'Name',
  namePlaceholder: 'POI Name',
  type: 'Type',
  orderPlaceholder: 'Order (lower = first)',
  map: 'Map',
  positionX: 'Position X',
  positionY: 'Position Y',
  positionXPlaceholder: 'X Position (0-1)',
  positionYPlaceholder: 'Y Position (0-1)',
  positionHelp: 'From 0 to 1, from the map\'s top left corner. Dragging the pin on the map sets them too.',
  description: 'Description',
  descriptionPlaceholder: 'POI Description',
  icon: 'Icon',
  mainImage: 'Main image',
  deleteTitle: 'Delete place',
  deleteConfirm: (name: string) => `Are you sure you want to delete the POI "${name}"?`,
  importFailed: 'Import failed',
  importFinished: 'Import finished',
  invalidFormat: 'Invalid JSON format. The file must contain a "pois" array.',
  noValidPois: 'No valid POIs found in the file.',
  parseError: 'Error parsing JSON file. Please check the file format.',
  /** Names a place without a name in the import errors */
  unknown: 'Unknown',
  /** `refused` places had the `errors`: a place can have several */
  imported: (count: number, refused: number, errors: string[]) =>
    `Successfully imported ${count} POIs.${refused > 0 ? `\n\n${refused} POIs were not imported due to errors:\n${errors.join('\n')}` : ''}`,
  clearAllTitle: 'Clear All POIs',
  clearAllConfirm: (count: number) => `Are you sure you want to delete all ${count} POIs? This action cannot be undone.`,
  clearAll: 'Clear All',
};

const lieux = (count: number) => `${count} ${count > 1 ? 'lieux' : 'lieu'}`;

const fr: typeof en = {
  remove: 'Supprimer',
  title: 'Lieux',
  subtitle: 'Les scènes et les points de restauration, placés sur vos plans. Faites glisser une épingle pour la déplacer, cliquez dessus pour modifier le lieu.',
  importJson: 'Importer un JSON',
  addPlace: 'Ajouter un lieu',
  clearAllMenu: 'Effacer tous les lieux',
  search: 'Rechercher des lieux...',
  stage: 'Scène',
  stages: 'Scènes',
  food: 'Restauration',
  timetableOrder: 'Ordre dans le planning',
  orderOf: (order: number) => `Ordre dans le planning\u00a0: ${order}`,
  noMatch: 'Aucun lieu ne correspond à cette recherche.',
  empty: 'Aucun lieu ici pour l\'instant.',
  untitledMap: 'Plan sans titre',
  notOnMap: 'Sans plan',
  notOnMapCount: (count: number) => `Sans plan · ${count}`,
  noMap: 'Aucun plan pour l\'instant. Ajoutez-en un dans l\'onglet Plans pour y placer des lieux.',
  goToMaps: 'Aller aux plans',
  mapAlt: 'Plan du festival',
  unattributed: 'Les lieux sans plan, ou dont le plan a été supprimé, apparaissent ici. Modifiez un lieu pour lui choisir un plan.',
  newPlace: 'Nouveau lieu',
  addTitle: 'Ajouter un lieu',
  editTitle: 'Modifier le lieu',
  name: 'Nom',
  namePlaceholder: 'Nom du lieu',
  type: 'Type',
  orderPlaceholder: 'Ordre (le plus petit en premier)',
  map: 'Plan',
  positionX: 'Position X',
  positionY: 'Position Y',
  positionXPlaceholder: 'Position X (0-1)',
  positionYPlaceholder: 'Position Y (0-1)',
  positionHelp: 'De 0 à 1, depuis le coin supérieur gauche du plan. Vous pouvez aussi les régler en faisant glisser l\'épingle sur le plan.',
  description: 'Description',
  descriptionPlaceholder: 'Description du lieu',
  icon: 'Icône',
  mainImage: 'Image principale',
  deleteTitle: 'Supprimer le lieu',
  deleteConfirm: (name: string) => `Voulez-vous vraiment supprimer le lieu «\u00a0${name}\u00a0»\u00a0?`,
  importFailed: 'Échec de l\'import',
  importFinished: 'Import terminé',
  invalidFormat: 'Format JSON non valide. Le fichier doit contenir un tableau «\u00a0pois\u00a0».',
  noValidPois: 'Aucun lieu valide dans le fichier.',
  parseError: 'Impossible de lire le fichier JSON. Vérifiez son format.',
  unknown: 'Inconnu',
  imported: (count: number, refused: number, errors: string[]) =>
    `${lieux(count)} importé${count > 1 ? 's' : ''} avec succès.${refused > 0 ? `\n\n${lieux(refused)} non importé${refused > 1 ? 's' : ''} à cause d'erreurs\u00a0:\n${errors.join('\n')}` : ''}`,
  clearAllTitle: 'Effacer tous les lieux',
  clearAllConfirm: (count: number) =>
    `Voulez-vous vraiment supprimer ${count > 1 ? `les ${count} lieux` : 'l\'unique lieu'}\u00a0? Cette action est irréversible.`,
  clearAll: 'Tout effacer',
};

const messages = { en, fr };
export default messages;
